import { AlertTriangle, Settings, X, BellOff } from 'lucide-react';
import { useUIStore } from '@/stores/ui-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useAppStore } from '@/stores/app-store';
import { INBOX_SUPPORTED_TPAS, EVENT_APPS, CALL_APPS } from '@/config/constants';

/**
 * Amber alert banner shown when background check detects incoming sync is not configured.
 * - "Enable" → navigates to Settings > Incoming Sync
 * - X → dismisses for the session
 * - "Don't show again" → persists to app preferences
 */
export function IncomingSyncAlert() {
  const show = useUIStore((s) => s.incomingSyncAlert);
  const dismiss = useUIStore((s) => s.setIncomingSyncAlert);
  const openSettings = useUIStore((s) => s.openSettings);
  const setActiveSettingsTab = useUIStore((s) => s.setActiveSettingsTab);
  const settingsOpen = useUIStore((s) => s.settingsOpen);
  const hideIncomingSyncAlert = useSettingsStore((s) => s.preferences.hideIncomingSyncAlert);

  const appCodeTPA = useAppStore((s) => s.appCodeTPA);

  if (!show || hideIncomingSyncAlert) return null;
  if (settingsOpen) return null;
  // Only show for inbox-supported or event TPAs — others have no incoming feature
  const isEventApp = appCodeTPA && (EVENT_APPS as readonly string[]).includes(appCodeTPA);
  const isCallApp = appCodeTPA && (CALL_APPS as readonly string[]).includes(appCodeTPA);
  if (appCodeTPA && !INBOX_SUPPORTED_TPAS.has(appCodeTPA) && !isEventApp) return null;
  if (isCallApp) return null;

  const handleEnable = () => {
    setActiveSettingsTab('incoming-sync');
    openSettings();
  };

  const handleDontShowAgain = () => {
    useSettingsStore.getState().updatePreferences({ hideIncomingSyncAlert: true });
    dismiss(false);
  };

  return (
    <div className="shrink-0 z-40 px-3 py-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-200">
      <div className="flex items-start gap-2">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        <p className="flex-1 text-xs leading-relaxed">
          You are <strong>not receiving</strong> incoming {isEventApp ? 'events' : 'messages'}. Enable incoming
          sync to capture {isEventApp ? 'incoming events' : 'customer messages'}.
        </p>
        <button
          onClick={() => dismiss(false)}
          className="p-0.5 text-amber-600/60 dark:text-amber-400/60 hover:text-amber-800 dark:hover:text-amber-200 transition-colors shrink-0"
          title="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex items-center gap-3 mt-1.5 ml-5.5 pl-px">
        <button
          onClick={handleEnable}
          className="inline-flex items-center gap-1 rounded-md bg-amber-600 dark:bg-amber-500 text-white px-2.5 py-1 text-[10px] font-medium hover:bg-amber-700 dark:hover:bg-amber-600 transition-colors"
        >
          <Settings className="h-3 w-3" />
          Enable
        </button>
        <button
          onClick={handleDontShowAgain}
          className="text-[10px] text-amber-700/70 dark:text-amber-300/70 hover:text-amber-800 dark:hover:text-amber-200 underline underline-offset-2 transition-colors"
        >
          Don't show again
        </button>
      </div>
    </div>
  );
}
